import { getSettings, replaceAllData } from "../db";
import { parseEnglishLevel } from "../level";
import type {
  AppSettings,
  BackupPayload,
  DiagnosisBlock,
  DiagnosisType,
  EnglishLevel,
  LearningStep,
  LevelHistoryEntry,
  ReadingLevel,
  ReadingRecord,
  ReadingStatus,
  SelectionAnnotation,
  UserLevelProfile,
} from "../types";
import { ensureXlsx } from "./loader";

const LEVELS = new Set<EnglishLevel>(["beginner", "intermediate", "advanced"]);
const READING_LEVELS = new Set<ReadingLevel>(["beginner", "intermediate", "advanced", "auto"]);
const STATUSES = new Set<ReadingStatus>(["draft", "diagnosing", "learning", "done"]);
const DIAG_TYPES = new Set<DiagnosisType>(["vocab", "phrase", "sentence", "logic", "background"]);

type Row = Record<string, unknown>;

function cell(row: Row, ...keys: string[]): string {
  for (const key of keys) {
    const value = row[key];
    if (value != null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return "";
}

function num(row: Row, ...keys: string[]): number {
  const raw = cell(row, ...keys);
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function boolYes(row: Row, ...keys: string[]): boolean {
  const raw = cell(row, ...keys).toLowerCase();
  return ["是", "yes", "y", "true", "1"].includes(raw);
}

export function parseSettingsSheet(rows: Row[]): AppSettings {
  const map = Object.fromEntries(rows.map((row) => [cell(row, "项", "key"), cell(row, "值", "value")]));
  const level = (map["默认英语水平"] || map.level || "intermediate") as EnglishLevel;
  return {
    level: LEVELS.has(level) ? level : "intermediate",
    ollamaApiUrl: map["AI 接口"] || map.ollamaApiUrl || "http://127.0.0.1:11434",
    ollamaModel: map["AI 模型"] || map.ollamaModel || "gemma3:latest",
    lastBackupAt: map["上次备份"] || map.lastBackupAt || null,
  };
}

function parseLevelHistory(raw: unknown): LevelHistoryEntry[] {
  if (typeof raw === "string" && raw.trim()) {
    try {
      raw = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((entry) => {
      const at = String((entry as LevelHistoryEntry)?.at ?? "").trim();
      const level = parseEnglishLevel((entry as LevelHistoryEntry)?.level);
      return at ? { at, level } : null;
    })
    .filter((entry): entry is LevelHistoryEntry => entry !== null)
    .slice(-30);
}

function parseUserLevelSheet(rows: Row[]): UserLevelProfile {
  const map = Object.fromEntries(rows.map((row) => [cell(row, "项", "key"), row["值"] ?? row.value]));
  const manualRaw = String(map["手动覆盖"] ?? map.manualOverride ?? "").trim();
  const confidence = Number(map["置信度"] ?? map.confidence);
  return {
    assessedLevel: parseEnglishLevel(map["评估水平"] ?? map.assessedLevel),
    levelReason: String(map["评估说明"] ?? map.levelReason ?? "").trim() || null,
    confidence: Number.isFinite(confidence) ? Math.max(0, Math.min(1, confidence)) : 0,
    lastAssessedAt: String(map["上次评估时间"] ?? map.lastAssessedAt ?? "").trim() || null,
    manualOverride: manualRaw && LEVELS.has(manualRaw as EnglishLevel) ? (manualRaw as EnglishLevel) : null,
    signals: {
      diagnosesCompleted: Number(map["诊断完成数"] ?? map.diagnosesCompleted) || 0,
      stepsCompleted: Number(map["步骤完成数"] ?? map.stepsCompleted) || 0,
      simplifyRequests: Number(map["求详解次数"] ?? map.simplifyRequests) || 0,
      checkQuestionsAnswered: Number(map["小测答题数"] ?? map.checkQuestionsAnswered) || 0,
      checkQuestionsCorrect: Number(map["小测正确数"] ?? map.checkQuestionsCorrect) || 0,
    },
    levelHistory: parseLevelHistory(map["水平历史"] ?? map.levelHistory),
  };
}

function parseReadings(rows: Row[]): ReadingRecord[] {
  return rows
    .map((row) => {
      const id = cell(row, "id");
      const text = cell(row, "原文", "text");
      if (!id || !text) {
        return null;
      }
      const level = cell(row, "水平", "level") as ReadingLevel;
      const status = cell(row, "状态", "status") as ReadingStatus;
      return {
        id,
        title: cell(row, "标题", "title") || "未命名阅读",
        text,
        selection: cell(row, "选中段落", "selection") || null,
        level: READING_LEVELS.has(level) ? level : "auto",
        diagnosisSummary: cell(row, "诊断摘要", "diagnosisSummary") || null,
        textTranslation: cell(row, "原文译文", "textTranslation") || null,
        status: STATUSES.has(status) ? status : "draft",
        currentStep: num(row, "当前步骤", "currentStep"),
        createdAt: cell(row, "创建时间", "createdAt") || new Date().toISOString(),
        updatedAt: cell(row, "更新时间", "updatedAt") || new Date().toISOString(),
      } satisfies ReadingRecord;
    })
    .filter((row): row is ReadingRecord => row !== null);
}

function parseDiagnosis(rows: Row[]): DiagnosisBlock[] {
  return rows
    .map((row) => {
      const id = cell(row, "id");
      const readingId = cell(row, "阅读id", "readingId");
      if (!id || !readingId) {
        return null;
      }
      const type = cell(row, "类型", "type") as DiagnosisType;
      const translation = cell(row, "译文", "translation");
      return {
        id,
        readingId,
        type: DIAG_TYPES.has(type) ? type : "vocab",
        excerpt: cell(row, "原文片段", "excerpt"),
        ...(translation ? { translation } : {}),
        note: cell(row, "说明", "note"),
        sort: num(row, "排序", "sort"),
      } satisfies DiagnosisBlock;
    })
    .filter((row): row is DiagnosisBlock => row !== null);
}

function parseSteps(rows: Row[]): LearningStep[] {
  return rows
    .map((row) => {
      const id = cell(row, "id");
      const readingId = cell(row, "阅读id", "readingId");
      if (!id || !readingId) {
        return null;
      }
      const completedAt = cell(row, "完成时间", "completedAt");
      return {
        id,
        readingId,
        stepIndex: num(row, "步骤序号", "stepIndex"),
        title: cell(row, "标题", "title"),
        content: cell(row, "内容", "content"),
        checkQuestion: cell(row, "小测题", "checkQuestion") || null,
        done: boolYes(row, "是否完成", "done"),
        completedAt: completedAt || null,
      } satisfies LearningStep;
    })
    .filter((row): row is LearningStep => row !== null);
}

export function parseAnnotations(rows: Row[]): SelectionAnnotation[] {
  return rows
    .map((row) => {
      const id = cell(row, "id");
      const readingId = cell(row, "阅读id", "readingId");
      const selectedText = cell(row, "选中文本", "selectedText");
      const answer = cell(row, "回答", "answer");
      if (!id || !readingId || !selectedText || !answer) {
        return null;
      }
      return {
        id,
        readingId,
        selectedText,
        answer,
        createdAt: cell(row, "创建时间", "createdAt") || new Date().toISOString(),
        updatedAt: cell(row, "更新时间", "updatedAt") || new Date().toISOString(),
      } satisfies SelectionAnnotation;
    })
    .filter((row): row is SelectionAnnotation => row !== null);
}

export type ImportPreview = {
  exportedAt: string | null;
  settings: AppSettings;
  userLevelProfile: UserLevelProfile;
  readings: ReadingRecord[];
  diagnosisBlocks: DiagnosisBlock[];
  learningSteps: LearningStep[];
  selectionAnnotations: SelectionAnnotation[];
};

export async function parseExcelFile(file: File): Promise<ImportPreview> {
  const XLSX = await ensureXlsx();
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: "array" });

  const sheet = (name: string) => {
    if (!wb.SheetNames.includes(name)) {
      return [] as Row[];
    }
    return XLSX.utils.sheet_to_json<Row>(wb.Sheets[name], { defval: "" });
  };

  const settingsRows = sheet("设置");
  const exportedAt = cell(settingsRows[0] ?? {}, "值", "value") || null;
  const userLevelRows = sheet("用户水平");

  return {
    exportedAt,
    settings: parseSettingsSheet(settingsRows),
    userLevelProfile: userLevelRows.length ? parseUserLevelSheet(userLevelRows) : parseUserLevelSheet([]),
    readings: parseReadings(sheet("阅读记录")),
    diagnosisBlocks: parseDiagnosis(sheet("诊断块")),
    learningSteps: parseSteps(sheet("学习步骤")),
    selectionAnnotations: parseAnnotations(sheet("划词笔记")),
  };
}

/** Preserve local apiKey when merging imported settings (apiKey is never exported). */
export function mergeImportSettings(imported: AppSettings, local: AppSettings): AppSettings {
  return { ...imported, apiKey: local.apiKey };
}

export async function applyReplaceImport(preview: ImportPreview): Promise<void> {
  const currentSettings = await getSettings();
  const payload: BackupPayload = {
    version: 1,
    exportedAt: preview.exportedAt ?? new Date().toISOString(),
    settings: mergeImportSettings(preview.settings, currentSettings),
    userLevelProfile: preview.userLevelProfile,
    readings: preview.readings,
    diagnosisBlocks: preview.diagnosisBlocks,
    learningSteps: preview.learningSteps,
    selectionAnnotations: preview.selectionAnnotations,
  };
  await replaceAllData(payload);
}
